/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import { Box, Button, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import type { UINotification } from '@poweralpha/common';
import type { InfiniteData } from '@tanstack/react-query';
import { type UIEvent, useMemo, useState } from 'react';
import type { UINotificationsQueryResult } from '../types';
import NotificationList from './NotificationList';
import { NotificationEmptyState, NotificationSkeletonRows } from './NotificationPlaceholders';
import NotificationTabs from './NotificationTabs';
import { getPopperTabs, type PopperTabKey } from './popperTabs';

const NOTIF_LIMIT = 100; // only page through the latest 100 in the popper
const LOAD_MORE_THRESHOLD = 200; // px from the bottom

interface Props {
  data: InfiniteData<UINotificationsQueryResult> | undefined;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onSelect: (notification: UINotification) => void;
  onMarkRead: (notification: UINotification) => void;
  onMarkAllRead: () => void;
  onOpenCenter: () => void;
}

function NotificationPopperContent({
  data,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  onSelect,
  onMarkRead,
  onMarkAllRead,
  onOpenCenter,
}: Readonly<Props>) {
  const [tabKey, setTabKey] = useState<PopperTabKey>('all');
  const tabs = useMemo(() => getPopperTabs(data), [data]);
  const tab = tabs.find(({ key }) => key === tabKey) ?? tabs[0];
  const hasUnread = (data?.pages.at(0)?.unread.length ?? 0) > 0;

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    if (tabKey !== 'all' || isFetchingNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const loaded = tab.notifications.length;
    const total = data?.pages.at(-1)?.count ?? 0;
    if (
      scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD &&
      loaded < total &&
      loaded < NOTIF_LIMIT
    ) {
      fetchNextPage();
    }
  };

  return (
    <Stack sx={{ width: 400, maxWidth: 'calc(100vw - 24px)' }}>
      <Stack direction="row" sx={{ alignItems: 'center', pl: 2, pr: 1.25, pt: 1.75, pb: 1.25 }}>
        <Typography component="h2" sx={{ flex: 1, fontSize: 15, fontWeight: 600 }}>
          Notifications
        </Typography>
        <Button
          size="small"
          startIcon={<DoneAllRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={onMarkAllRead}
          disabled={!hasUnread}
          sx={{ fontSize: 12, textTransform: 'none' }}
        >
          Mark all read
        </Button>
      </Stack>
      <NotificationTabs tabs={tabs} value={tab.key} onChange={setTabKey} />
      <Divider />
      <Box onScroll={handleScroll} sx={{ maxHeight: 560, overflowY: 'auto', px: 0.75, py: 0.5 }}>
        {isLoading && <NotificationSkeletonRows />}
        {!isLoading && tab.notifications.length === 0 && (
          <NotificationEmptyState
            action={
              tab.key !== 'all' ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setTabKey('all')}
                  sx={{ textTransform: 'none' }}
                >
                  Show all notifications
                </Button>
              ) : undefined
            }
          />
        )}
        <NotificationList
          notifications={tab.notifications}
          onSelect={onSelect}
          onMarkRead={onMarkRead}
        />
        {isFetchingNextPage && <NotificationSkeletonRows count={1} />}
      </Box>
      <Divider />
      <ButtonBase
        onClick={onOpenCenter}
        sx={{
          justifyContent: 'flex-start',
          gap: 1,
          px: 2,
          py: 1.25,
          fontSize: 12.5,
          fontWeight: 500,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        Open notification center
        <ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />
      </ButtonBase>
    </Stack>
  );
}

export default NotificationPopperContent;
